// ============================================
// 2026-10-19（M3）：市场预设（标绘模板 / 体积预设）——筛选 + 应用草稿生成（纯函数，可单测）
//  - 服务端契约见 dtwin-server plugins/market-preset.ts（上传/上架强校验；此处防御性再校验）
//  - 落位语义：关键点/位置按包围盒中心 xz 平移到原点（保留 y），模板建议围绕原点设计
//  - 红线（方案 §10.6）：仅平台下发预设，不做第三方预设包本地导入入口
// ============================================
import type { MarketComponentItem } from '../plugins/plugin-api';
import { createChart3DObject, type Chart3DObject, type Chart3DKind } from '../types/chart3d';

export type P3 = [number, number, number];

/** 标绘模板（category=annotation-template） */
export function annotationTemplatesOf(list: MarketComponentItem[]): MarketComponentItem[] {
  return (list || []).filter((c) => c.kind === 'preset' && c.category === 'annotation-template' && c.presetPayload);
}

/** 体积预设（category=volume-preset） */
export function volumePresetsOf(list: MarketComponentItem[]): MarketComponentItem[] {
  return (list || []).filter((c) => c.kind === 'preset' && c.category === 'volume-preset' && c.presetPayload);
}

/** 预设价格标签（UI 徽标） */
export function presetPriceLabel(item: MarketComponentItem): { text: string; color: string } {
  if (item.priceType === 'credits') {
    return { text: `积分 ${Number(item.creditPrice ?? 0)}/次`, color: item.entitled === false ? 'red' : 'orange' };
  }
  if (item.priceType === 'paid') {
    return { text: item.entitled === false ? '需开通' : '付费', color: item.entitled === false ? 'red' : 'gold' };
  }
  return { text: '免费', color: 'green' };
}

function isFiniteP3(v: unknown): v is P3 {
  return Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === 'number' && Number.isFinite(n));
}

/** 关键点集合 → xz 中心平移（保留 y；空集合原样返回） */
export function centerNormalizeXZ(pointSets: P3[][]): P3[][] {
  const all = pointSets.flat();
  if (!all.length) return pointSets;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of all) {
    minX = Math.min(minX, p[0]);
    maxX = Math.max(maxX, p[0]);
    minZ = Math.min(minZ, p[2]);
    maxZ = Math.max(maxZ, p[2]);
  }
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  if (!Number.isFinite(cx) || !Number.isFinite(cz)) return pointSets;
  return pointSets.map((pts) => pts.map((p) => [p[0] - cx, p[1], p[2] - cz] as P3));
}

// ---------- 标绘模板 ----------

export interface AnnotationDraft {
  kind: string;
  name: string;
  points: P3[];
  planeMode: 'ground' | 'model' | 'free';
  style?: Record<string, unknown>;
  animation?: { grow?: boolean; fade?: boolean };
}

/** 模板负载 → 标绘草稿（防御性校验；非法抛 Error，由面板转 message） */
export function templateToAnnotationDrafts(item: MarketComponentItem): AnnotationDraft[] {
  const payload = item.presetPayload as { annotations?: unknown } | null;
  const list = payload?.annotations;
  if (!Array.isArray(list) || list.length === 0) throw new Error('模板负载缺少 annotations 数组');
  if (list.length > 12) throw new Error('模板标绘数量超限（≤12）');
  const drafts = list.map((raw, i) => {
    const a = raw as Record<string, unknown>;
    const kind = String(a?.kind || '');
    if (!kind) throw new Error(`annotations[${i}].kind 缺失`);
    const pointsRaw = Array.isArray(a.points) ? (a.points as unknown[]) : [];
    const points = pointsRaw.filter(isFiniteP3) as P3[];
    return {
      kind,
      name: String(a.name || item.name || `模板标绘 ${i + 1}`),
      points,
      planeMode: (['ground', 'model', 'free'].includes(String(a.planeMode)) ? String(a.planeMode) : 'ground') as AnnotationDraft['planeMode'],
      style: a.style && typeof a.style === 'object' ? (a.style as Record<string, unknown>) : undefined,
      animation: a.animation && typeof a.animation === 'object' ? (a.animation as { grow?: boolean; fade?: boolean }) : undefined,
    };
  });
  const normalized = centerNormalizeXZ(drafts.map((d) => d.points));
  return drafts.map((d, i) => ({ ...d, points: normalized[i] }));
}

// ---------- 体积预设 ----------

export interface VolumeDraft {
  kind: string;
  name: string;
  enabled: boolean;
  shape: 'box' | 'sphere' | 'cylinder';
  position: P3;
  size: P3;
  blendDistance?: number;
  priority?: number;
  envs?: Record<string, number | boolean | string>;
  ray?: Record<string, unknown>;
  vol?: Record<string, unknown>;
}

/** 模板负载 → 体积草稿（防御性校验；非法抛 Error） */
export function templateToVolumeDrafts(item: MarketComponentItem): VolumeDraft[] {
  const payload = item.presetPayload as { volumes?: unknown } | null;
  const list = payload?.volumes;
  if (!Array.isArray(list) || list.length === 0) throw new Error('预设负载缺少 volumes 数组');
  if (list.length > 8) throw new Error('预设体积数量超限（≤8）');
  const drafts = list.map((raw, i) => {
    const v = raw as Record<string, unknown>;
    const kind = String(v?.kind || '');
    if (!kind) throw new Error(`volumes[${i}].kind 缺失`);
    const size = isFiniteP3(v.size) ? (v.size as P3) : ([5, 5, 5] as P3);
    const position = isFiniteP3(v.position) ? (v.position as P3) : ([0, 0, 0] as P3);
    return {
      kind,
      name: String(v.name || item.name || `预设体积 ${i + 1}`),
      enabled: true,
      shape: (['box', 'sphere', 'cylinder'].includes(String(v.shape)) ? String(v.shape) : 'box') as VolumeDraft['shape'],
      position,
      size,
      blendDistance: typeof v.blendDistance === 'number' ? v.blendDistance : undefined,
      priority: typeof v.priority === 'number' ? v.priority : undefined,
      envs: v.envs && typeof v.envs === 'object' ? (v.envs as Record<string, number | boolean | string>) : undefined,
      ray: v.ray && typeof v.ray === 'object' ? (v.ray as Record<string, unknown>) : undefined,
      vol: v.vol && typeof v.vol === 'object' ? (v.vol as Record<string, unknown>) : undefined,
    };
  });
  // 位置 xz 居中（保留 y：雾盒/云团的高度语义不破坏）
  const normalized = centerNormalizeXZ(drafts.map((d) => [d.position]));
  return drafts.map((d, i) => ({ ...d, position: normalized[i][0] }));
}

const CHART_TEMPLATE_KINDS: Chart3DKind[] = [
  'bar3d', 'scatter3d', 'surface3d', 'line3d',
  'pie3d', 'donut3d', 'gauge3d', 'radar3d', 'funnel3d',
  'board3d', 'map3d', 'globe3d',
];

export function chartTemplatesOf(list: MarketComponentItem[]): MarketComponentItem[] {
  return (list || []).filter((c) => c.kind === 'preset' && c.category === 'chart-template' && c.presetPayload);
}

export function templateToChartDrafts(item: MarketComponentItem): Chart3DObject[] {
  const payload = item.presetPayload as { charts?: unknown } | null;
  const list = payload?.charts;
  if (!Array.isArray(list) || list.length === 0) throw new Error('模板负载缺少 charts 数组');
  if (list.length > 8) throw new Error('模板图表数量超限（≤8 个）');
  const drafts = list.map((raw, index) => {
    const source = raw as Record<string, unknown>;
    const kind = String(source?.kind || '') as Chart3DKind;
    if (!CHART_TEMPLATE_KINDS.includes(kind)) throw new Error(`charts[${index}].kind 非法：${kind || '空'}`);
    const { id: _id, ...rest } = source;
    return createChart3DObject({
      ...(rest as Partial<Chart3DObject>),
      kind,
      name: String(source.name || item.name || `模板图表 ${index + 1}`),
    });
  });
  const normalized = centerNormalizeXZ(drafts.map((chart) => [chart.position]));
  return drafts.map((chart, index) => ({ ...chart, position: normalized[index][0] }));
}
